import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

import { nodeGet } from '@uncinc/react-drupal-core';

import { setFlag, removeFlag } from '../../store/actions/Entity/Flag';
import { viewGet } from '../../store/actions/Entity/View';
import useTypedDispatch from '../../hooks/useTypedDispatch';
import useClasses from '../../hooks/useClasses';
import usePopup from '../../hooks/usePopup';
import Button from '../Button';
import FeedbackPopup from './Popup';

import './style.scss';

const FLAG_UPVOTE = 'upvote_tip';
const FLAG_DOWNVOTE = 'downvote_tip';

type FeedbackAnswer = typeof FLAG_UPVOTE | typeof FLAG_DOWNVOTE | null;

type FeedbackProps = {
  title?: string;
  parentEntityType: string;
  parentEntityId: number;
  upvote?: boolean;
  downvote?: boolean;
};

const Feedback: React.FC<FeedbackProps> = ({
  title,
  parentEntityType,
  parentEntityId,
  upvote = false,
  downvote = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useTypedDispatch();
  const { buildClass } = useClasses('Feedback');

  let initialAnswer: FeedbackAnswer = null;
  if (upvote) {
    initialAnswer = FLAG_UPVOTE;
  } else if (downvote) {
    initialAnswer = FLAG_DOWNVOTE;
  }
  const [givenAnswer, setGivenAnswer] = useState<FeedbackAnswer>(initialAnswer);
  const [loading, setLoading] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);

  const popup = usePopup(`FeedbackPopup-${parentEntityId}`, {
    closeOnOutsideClick: true,
  });

  const getButtonState = useCallback((flag: FeedbackAnswer) => {
    if (loading) {
      return 'disabled';
    }
    if (givenAnswer === flag) {
      return 'active';
    }
    return undefined;
  }, [loading, givenAnswer]);

  const flagCallback = useCallback(async (flagType: FeedbackAnswer) => {
    if (!flagType || loading) return;

    const otherFlagType = flagType === FLAG_UPVOTE ? FLAG_DOWNVOTE : FLAG_UPVOTE;
    const previousAnswer = givenAnswer;

    setLoading(true);

    try {
      if (givenAnswer === flagType) {
        // Toggle off: remove current flag.
        setGivenAnswer(null);
        await dispatch(removeFlag(flagType, parentEntityType, parentEntityId));
      } else {
        // Switch or new vote.
        setGivenAnswer(flagType);
        if (previousAnswer !== null) {
          // Remove the opposing flag first.
          await dispatch(removeFlag(otherFlagType, parentEntityType, parentEntityId));
        }
        await dispatch(setFlag(flagType, parentEntityType, parentEntityId));
      }
      dispatch(nodeGet(parentEntityId));
      // Refresh the tips view so the sort order and upvote counts are up to date.
      if (flagType === FLAG_UPVOTE || previousAnswer === FLAG_UPVOTE) {
        dispatch(viewGet('tips', 'block_1'));
      }
    } catch (error) {
      setGivenAnswer(previousAnswer);
    }
    setLoading(false);
  }, [loading, givenAnswer, dispatch, parentEntityType, parentEntityId]);

  const flagCallbackWithReason = useCallback(async (values: { body: string }) => {
    const previousAnswer = givenAnswer;

    setPopupLoading(true);

    try {
      // First remove the downvote without reason.
      if (givenAnswer === FLAG_DOWNVOTE) {
        await dispatch(removeFlag(FLAG_DOWNVOTE, parentEntityType, parentEntityId));
      }
      // Set the downvote with reason.
      await dispatch(setFlag(FLAG_DOWNVOTE, parentEntityType, parentEntityId, {
        field_reason: [{ value: values.body }],
      }));
      setGivenAnswer(FLAG_DOWNVOTE);
    } catch (error) {
      setGivenAnswer(previousAnswer);
    }
    setPopupLoading(false);
    popup.close();
  }, [givenAnswer, dispatch, parentEntityType, parentEntityId, popup]);

  return (
    <div className={buildClass()}>
      <h2 className="asH4">
        {title ?? t('feedback.title')}
      </h2>
      <div className={buildClass('answers')}>
        <Button
          buttonState={getButtonState(FLAG_UPVOTE)}
          className={buildClass('button')}
          secondary
          icon="thumbs-up"
          onClick={() => { flagCallback(FLAG_UPVOTE).catch(() => {}); }}
        >
          {t('feedback.buttons.positive')}
        </Button>
        <Button
          buttonState={getButtonState(FLAG_DOWNVOTE)}
          className={buildClass('button')}
          secondary
          icon="thumbs-up"
          iconOrientation="left"
          onClick={() => { flagCallback(FLAG_DOWNVOTE).catch(() => {}); }}
        >
          {t('feedback.buttons.negative')}
        </Button>
      </div>
      {givenAnswer !== null && (
        <div className={buildClass('response')}>
          {givenAnswer === FLAG_UPVOTE && (
            <p>{t('feedback.messages.positive')}</p>
          )}
          {givenAnswer === FLAG_DOWNVOTE && (
            <>
              <p>{t('feedback.messages.negative')}</p>
              <Button
                className={buildClass('button')}
                secondary
                icon="support"
                onClick={() => { popup.open(); }}
              >
                {t('feedback.buttons.contact')}
              </Button>
            </>
          )}
        </div>
      )}
      {popup.isOpen && (
        ReactDOM.createPortal(
          <FeedbackPopup
            loading={popupLoading}
            onSubmit={(values) => { flagCallbackWithReason(values).catch(() => {}); }}
            {...popup.popupProps}
          />,
          document.body,
        )
      )}
    </div>
  );
};

export default Feedback;
